import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { forwardRef } from 'react';
import { Box } from '../Box/index.js';
import { Icon } from '../Icon/index.js';
import { Loading as Loading$1 } from '../Loading/index.js';
import { getButtonStyles, getIconSize, getButtonSizes, getTertiarySizes, checkForTertiaryVariant } from './styles.js';

/* ======================================= */
const ButtonIcon = _ref => {
  let {
    name,
    size,
    ...rest
  } = _ref;
  return /*#__PURE__*/React__default.createElement(Icon, _extends({
    name: name,
    size: size === 'small' ? 'medium' : 'large'
  }, rest));
};
const IconLeft = _ref2 => {
  let {
    iconLeft,
    isLoading,
    size
  } = _ref2;
  if (!iconLeft || isLoading) return null;
  return /*#__PURE__*/React__default.createElement(ButtonIcon, {
    "aria-hidden": true,
    mr: "space30",
    name: iconLeft,
    size: size
  });
};
const IconRight = _ref3 => {
  let {
    iconRight,
    isLoading,
    size
  } = _ref3;
  if (!iconRight || isLoading) return null;
  return /*#__PURE__*/React__default.createElement(ButtonIcon, {
    "aria-hidden": true,
    ml: "space30",
    name: iconRight,
    size: size
  });
};
const Loading = _ref4 => {
  let {
    isLoading,
    loadingText
  } = _ref4;
  if (!isLoading) return null;
  if (isLoading && loadingText) {
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Loading$1, {
      "aria-label": undefined,
      color: "currentColor",
      mr: "space40"
    }), loadingText);
  }
  return /*#__PURE__*/React__default.createElement(Loading$1, {
    color: "currentColor"
  });
};
/* ======================================= */
/**
 * The Button component is used to trigger an action or event, such as submitting a form,
 * opening a dialog, canceling an action, or performing a delete operation.
 *
 * ### Documentation
 * - [Examples](https://blockparty.endpoint.com/components/button#usage)
 * - [Props](https://blockparty.endpoint.com/components/button#props)
 * <br />
 *
 */
const Button = /*#__PURE__*/forwardRef((_ref5, forwardedRef) => {
  let {
    type = 'button',
    dataTestId = 'button',
    children,
    isDisabled,
    isLoading,
    loadingText,
    iconLeft,
    iconRight,
    size = 'large',
    variant = 'solid',
    variantColor = 'brandPrimary',
    ...rest
  } = _ref5;
  const _isDisabled = isDisabled || isLoading;
  const buttonStyles = getButtonStyles({
    variant,
    variantColor
  });
  const isTertiaryVariant = checkForTertiaryVariant(variant);
  const buttonSizes = !isTertiaryVariant && getButtonSizes(size);
  const tertiarySizes = isTertiaryVariant && getTertiarySizes(size);
  const iconSize = getIconSize(isTertiaryVariant, size);
  return /*#__PURE__*/React__default.createElement(Box, _extends({
    ref: forwardedRef,
    as: "button",
    "data-test-id": dataTestId
    // @ts-ignore false positive with TS compiler
    ,
    disabled: _isDisabled,
    type: type
  }, buttonStyles, buttonSizes, tertiarySizes, rest), /*#__PURE__*/React__default.createElement(IconLeft, {
    iconLeft: iconLeft,
    isLoading: isLoading,
    size: iconSize
  }), isLoading ? /*#__PURE__*/React__default.createElement(Loading, {
    isLoading: isLoading,
    loadingText: loadingText
  }) : children && /*#__PURE__*/React__default.createElement(Box, {
    as: "span",
    display: "inherit",
    whiteSpace: "nowrap"
  }, children), /*#__PURE__*/React__default.createElement(IconRight, {
    iconRight: iconRight,
    isLoading: isLoading,
    size: iconSize
  }));
});

export { Button, ButtonIcon, IconLeft, IconRight, Loading };
